import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, SxProps, TextField, Theme, Typography } from '@mui/material'
import { ScrollWrapper } from '../ScrollWrapper'

const TEXTAREA_FONT = {
    fontSize: '16px !important',
    fontWeight: '400 !important',
    lineHeight: '1.5 !important',
    fontFamily: 'inherit !important',
} as const

type AdvancedTextareaProps = {
    value?: string
    placeholder?: string
    disabled?: boolean
    isSaving?: boolean
    onSave: (value: string) => Promise<void> | void
    maxHeight?: number
    minRows?: number
    backgroundColor?: string
    borderColor?: string
    textColor?: string
    placeholderColor?: string
    spinnerColor?: string
    saveOnBlur?: boolean
    containerSx?: SxProps<Theme>
    contentSx?: SxProps<Theme>
    textFieldSx?: SxProps<Theme>
    displayTextSx?: SxProps<Theme>
}

export const AdvancedTextarea = ({
    value = '',
    placeholder = 'Enter text',
    disabled = false,
    isSaving = false,
    onSave,
    maxHeight = 104,
    minRows = 4,
    backgroundColor = 'figma.grayscale.contentWrapper',
    borderColor = 'figma.grayscale.stroke',
    textColor = 'figma.grayscale.tier3',
    placeholderColor = 'figma.grayscale.tier4',
    spinnerColor = 'inherit',
    saveOnBlur = true,
    containerSx,
    contentSx,
    textFieldSx,
    displayTextSx,
}: AdvancedTextareaProps) => {
    const shouldSkipNextBlurCommitRef = useRef(false)
    const [draftValue, setDraftValue] = useState(value)
    const [isEditing, setIsEditing] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)

    useEffect(() => {
        if (!isEditing) {
            setDraftValue(value)
            setSaveError(null)
        }
    }, [isEditing, value])

    const persistValue = useCallback(async () => {
        if (draftValue === value || isSaving) {
            setSaveError(null)
            setIsEditing(false)
            return
        }

        try {
            await onSave(draftValue)
            setSaveError(null)
            setIsEditing(false)
        } catch (error) {
            setSaveError('Save failed. Retry with Enter or blur.')
            setIsEditing(true)
        }
    }, [draftValue, isSaving, onSave, value])

    const cancelEditing = useCallback(() => {
        shouldSkipNextBlurCommitRef.current = true
        setDraftValue(value)
        setIsEditing(false)
    }, [value])

    const handleBlur = async () => {
        if (shouldSkipNextBlurCommitRef.current) {
            shouldSkipNextBlurCommitRef.current = false
            return
        }

        if (!saveOnBlur) {
            setIsEditing(false)
            return
        }

        await persistValue()
    }

    const hasValue = value.trim().length > 0

    return (
        <Box
            sx={[
                {
                    bgcolor: backgroundColor,
                    border: '1px solid',
                    borderColor,
                    borderRadius: 2,
                    p: 2,
                    position: 'relative',
                },
                ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
            ]}
            onClick={() => {
                if (disabled || isSaving || isEditing) {
                    return
                }

                setIsEditing(true)
            }}
        >
            {isSaving && (
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                    <CircularProgress size={14} color="inherit" sx={{ color: spinnerColor }} />
                </Box>
            )}

            <Box
                sx={[
                    {
                        position: 'relative',
                    },
                    ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
                ]}
            >
                {isEditing ? (
                    <Box sx={{ overflowY: 'visible' }}>
                        <TextField
                            value={draftValue}
                            placeholder={placeholder}
                            variant="standard"
                            fullWidth
                            multiline
                            minRows={minRows}
                            autoFocus
                            disabled={disabled || isSaving}
                            onChange={(event) => {
                                setDraftValue(event.target.value)
                                if (saveError) {
                                    setSaveError(null)
                                }
                            }}
                            onBlur={() => void handleBlur()}
                            onKeyDown={(event) => {
                                const input = event.currentTarget

                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    void persistValue()
                                    shouldSkipNextBlurCommitRef.current = true
                                    input.blur()
                                }

                                if (event.key === 'Escape') {
                                    cancelEditing()
                                    input.blur()
                                }
                            }}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    alignItems: 'flex-start',
                                    color: textColor,
                                    ...TEXTAREA_FONT,
                                },
                            }}
                            inputProps={{
                                sx: {
                                    ...TEXTAREA_FONT,
                                    overflow: 'visible',
                                    p: 0,
                                    '&::placeholder': {
                                        ...TEXTAREA_FONT,
                                        color: placeholderColor,
                                        opacity: 1,
                                    },
                                },
                            }}
                            sx={[
                                {
                                    width: '100%',
                                    '& .MuiInputBase-root': {
                                        p: 0,
                                        m: 0,
                                        width: '100%',
                                    },
                                },
                                ...(Array.isArray(textFieldSx) ? textFieldSx : [textFieldSx]),
                            ]}
                        />
                    </Box>
                ) : (
                    <ScrollWrapper
                        arrowColor="figma.grayscale.tier4"
                        fadeColor={backgroundColor}
                        arrowOffset={12}
                        arrowSize="10px"
                        containerSx={{ maxHeight }}
                    >
                        <Typography
                            sx={[
                                {
                                    ...TEXTAREA_FONT,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    color: hasValue ? textColor : placeholderColor,
                                    cursor: disabled ? 'default' : 'text',
                                },
                                ...(Array.isArray(displayTextSx) ? displayTextSx : [displayTextSx]),
                            ]}
                        >
                            {hasValue ? value : placeholder}
                        </Typography>
                    </ScrollWrapper>
                )}
            </Box>

            {!!saveError && (
                <Typography
                    variant="12x400"
                    color="error.main"
                    sx={{
                        mt: 1,
                        overflowWrap: 'anywhere',
                    }}
                >
                    {saveError}
                </Typography>
            )}
        </Box>
    )
}

export default AdvancedTextarea
